import {
    AppContentLocation,
    AppContentType,
    AppSection,
    AppViewType,
    ContentStatus,
    CreateAppSectionMutationVariables,
    Merchant,
    MerchantStatus,
    UpdateAppSectionMutationVariables
} from '@/__generated__/types';
import { CommonInput } from '@/components/_dashboard/promotion/formik/commonInput';
import { getErrorMessage } from '@/utils/stringHelper';
import LoadingButton from '@mui/material/Button';
import { Box, Button, Grid, Tab, Tabs, Typography } from '@mui/material';
import { Form, FormikProvider, useFormik } from 'formik';
import { get } from 'lodash';
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';

import { renderContent, tabContent } from '@/components/_dashboard/promotion/create/tabs/tabContent';
import { CommonSelect } from '@/components/_dashboard/promotion/formik/commonSelect';
import { DeepKeyOf } from '@/components/_dashboard/promotion/formik/type';
import { CommonTable } from '@/components/commonComponents/commonTable/commonTable';
import { ICommonTableHeadColumn } from '@/components/commonComponents/commonTable/commonTableHead/commonTableHead';
import { ISearchBox } from '@/components/commonComponents/commonTable/commonTableToolbar/commonTableToolbar';
import { useSnackbarHelper } from '@/components/useSnackbarHelper';
import { createAppSectionMutation, fetchAPPSections, updateAppSectionMutation } from '@/redux/slices/appSection';
import { queryAllMerchants } from '@/redux/slices/merchant';
import { dispatch, RootState, useSelector } from '@/redux/store';
import { appSectionValidationSchema } from '../appSectionValidationSchema';
import { convertAppSectionInput, initAppSection } from '../tools';
import { APPSectionFormTableRow } from './APPSectionFormTableRow';

interface AppSectionFormDialogContentProps {
    section?: AppSection;
    setIsDialogOpen: (isOpen: boolean) => void;
}
const MERCHANT_TABLE_HEAD: ICommonTableHeadColumn<Merchant>[] = [
    { property: null, label: '', align: 'left' },
    { property: 'name', label: 'Name', align: 'left' },
    { property: 'type', label: 'Type', align: 'left' }
];

const SEARCH_BOX: ISearchBox<Merchant> = {
    list: ['name', 'altName'],
    placeholder: 'Search by name, altName'
};

export const AppSectionFormDialogContent = (props: AppSectionFormDialogContentProps): JSX.Element => {
    const { t } = useTranslation();
    const { showSnackbar } = useSnackbarHelper();
    const { section, setIsDialogOpen } = props;
    const { appSections } = useSelector((state: RootState) => state.appSection);
    const { merchantList } = useSelector((state: RootState) => state.merchant);
    const [filteredMerchantList, setFilteredMerchantList] = useState<Merchant[]>(merchantList);
    const [sort, setSort] = useState<number>(0);
    const [tabIndex, setTabIndex] = useState(0);
    const [appSection] = useState<AppSection>(section || initAppSection);
    const [isLoading, setIsLoading] = useState(false);

    const isEdit = section ? true : false;
    const formik = useFormik<AppSection>({
        enableReinitialize: true,
        initialValues: appSection,
        validationSchema: appSectionValidationSchema,
        onSubmit: async () => {}
    });
    const { values, setFieldValue, setFieldTouched } = formik;
    const { merchants } = values;

    const handleTabIndexOnChange = (_: React.SyntheticEvent, newValue: number): void => {
        setTabIndex(newValue);
    };

    const queryAppSections = useCallback(async () => {
        try {
            setIsLoading(true);
            const resp = await dispatch(fetchAPPSections()).unwrap();
            if (resp) {
                showSnackbar('query app sections success', 'success');
                return;
            }
            showSnackbar('query app sections fail', 'error');
        } catch (error) {
            showSnackbar('query app sections fail', 'error');
            const errorMessage = getErrorMessage(error);
            console.error(errorMessage);
        } finally {
            setIsLoading(false);
        }
    }, [showSnackbar]);

    const updateSection = useCallback(async () => {
        try {
            setIsLoading(true);
            const input: UpdateAppSectionMutationVariables = {
                sectionId: values.id,
                section: convertAppSectionInput(values)
            };
            const resp = await dispatch(updateAppSectionMutation(input)).unwrap();
            if (resp && section) {
                await dispatch(queryAppSections);
                showSnackbar('Update Success', 'success');
                setIsLoading(false);
                setIsDialogOpen(false);
                return;
            }
            showSnackbar('Update Fail', 'error');
        } catch (error) {
            showSnackbar('Update Fail', 'error');
            const errorMessage = getErrorMessage(error);
            console.error(errorMessage);
            setIsLoading(false);
        }
    }, [values, setIsLoading, showSnackbar, setIsDialogOpen, queryAppSections]);

    const createAppSection = useCallback(async () => {
        try {
            setIsLoading(true);
            const input: CreateAppSectionMutationVariables = {
                section: convertAppSectionInput(values)
            };
            input.section.sort = sort;
            const resp = await dispatch(createAppSectionMutation(input)).unwrap();
            if (resp) {
                showSnackbar('Create Success', 'success');
                await dispatch(queryAppSections);
                setIsLoading(false);
                setIsDialogOpen(false);
                return;
            }
            showSnackbar('Create Fail', 'error');
        } catch (error) {
            showSnackbar('Create Fail', 'error');
            const errorMessage = getErrorMessage(error);
            console.error(errorMessage);
            setIsLoading(false);
        }
    }, [values, sort, setIsLoading, showSnackbar, setIsDialogOpen, queryAppSections]);

    const setFieldTouchedName = useCallback(
        async (name: DeepKeyOf<AppSection>) => {
            if (!name) return;
            const formikErrors = await setFieldTouched(name, true, true);
            if (!formikErrors) return;
            const error = get(formikErrors, name) as string | undefined;
            if (!error) return;
            throw new Error(error);
        },
        [setFieldTouched]
    );

    const createSection = async (): Promise<void> => {
        // pass the validation?
        // set the touched on all fields
        try {
            await setFieldTouchedName('title');
            await setFieldTouchedName('radiumInKm');
            await setFieldTouchedName('coordinate.latitude');
            await setFieldTouchedName('coordinate.longitude');
            if (isEdit) {
                updateSection();
            } else {
                createAppSection();
            }
        } catch (error) {
            const errorMessage = getErrorMessage(error);
            console.error(errorMessage);
            return;
        }
    };

    const queryMerchants = useCallback(async () => {
        try {
            await dispatch(queryAllMerchants()).unwrap();
        } catch (error) {
            showSnackbar('An unknown error occurred', 'error');
            const errorMessage = getErrorMessage(error);
            console.error(errorMessage);
        } finally {
            setIsLoading(false);
        }
    }, [showSnackbar]);

    const checkBoxOnChange = (merchant: Merchant): void => {
        const merchantId = merchant.merchantId;

        const currentMerchants = (merchants ?? []).filter((m) => m !== null);

        const validMerchants = currentMerchants.filter((m): m is Merchant => m !== null);

        const isMerchantSelected = validMerchants.some((m) => m.merchantId === merchantId);

        if (validMerchants.length === 0) {
            setFieldValue('merchants', [merchant]);
            return;
        }

        if (isMerchantSelected) {
            setFieldValue(
                'merchants',
                validMerchants.filter((m) => m.merchantId !== merchantId)
            );
            return;
        }

        if (currentMerchants.length >= 4) {
            showSnackbar('Maximum 4 merchants for the list', 'error');
            return;
        }

        setFieldValue('merchants', [...currentMerchants, merchant]);
    };

    useEffect(() => {
        queryMerchants();
    }, [queryMerchants]);

    useEffect(() => {
        // find the merchant is active
        const filteredMerchants = merchantList.filter((merchant) => merchant.merchantStatus === MerchantStatus.Active);
        setFilteredMerchantList(filteredMerchants);
    }, [merchantList, setFilteredMerchantList]);

    useEffect(() => {
        if (appSections.length === 0) {
            setSort(0);
            return;
        }
        const sortList = appSections.map((section) => section.sort);
        let maxSort = 0;
        if (sortList.length > 0) {
            maxSort = Math.max(...sortList);
        }
        setSort(maxSort + 1);
    }, [appSections]);

    const commonContents: tabContent[] = [
        {
            mdSize: 12,
            node: <CommonInput formik={formik} fieldPath={'title'} label="Title" placeholder="Please enter title" />
        },
        {
            mdSize: 12,
            node: (
                <CommonInput
                    formik={formik}
                    fieldPath={'description'}
                    label="Description"
                    placeholder="Please enter the description"
                    multiline={true}
                />
            )
        },
        {
            node: <CommonSelect formik={formik} fieldPath={'contentType'} options={AppContentType} label="Content Type" />,
            mdSize: 6
        },
        {
            node: <CommonSelect formik={formik} fieldPath={'location'} options={AppContentLocation} label="Location" />,
            mdSize: 6
        },
        {
            node: <CommonSelect formik={formik} fieldPath={'viewType'} options={AppViewType} label="View" />,
            mdSize: 6
        },
        {
            node: <CommonSelect formik={formik} fieldPath={'status'} label="Status" options={ContentStatus} />,
            mdSize: 6
        },
        {
            node: <CommonInput formik={formik} fieldPath={'radiumInKm'} type={'number'} label="Radium In Km" />,
            mdSize: 12
        },
        {
            node: <CommonInput formik={formik} fieldPath={'coordinate.latitude'} label="Latitude" type={'number'} />,
            mdSize: 6
        },
        {
            node: <CommonInput formik={formik} fieldPath={'coordinate.longitude'} label="Longitude" type={'number'} />,
            mdSize: 6
        }
    ];

    return (
        <Box sx={{ height: '100%' }}>
            <Tabs
                value={tabIndex}
                onChange={handleTabIndexOnChange}
                indicatorColor="primary"
                textColor="primary"
                variant="fullWidth"
                aria-label="action tabs example"
                sx={{ mb: '20px' }}
            >
                <Tab label={t('Information')} iconPosition="end" />
                <Tab label={t('Merchants')} iconPosition="end" />
            </Tabs>
            {tabIndex === 0 && (
                <FormikProvider value={formik}>
                    <Form noValidate autoComplete="off" onSubmit={createSection}>
                        <Grid sx={{ width: '100%' }} container spacing={1}>
                            {renderContent(commonContents)}
                        </Grid>
                    </Form>
                </FormikProvider>
            )}
            {tabIndex === 1 && (
                <Box>
                    <Typography sx={{ marginLeft: '24px' }}>{t('Maximum 4 merchants for the list: ')}</Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 1, ml: 2, mt: 1, mb: 1 }}>
                        {(merchants ?? [])
                            .filter((m): m is Merchant => m !== null)
                            .map((merchant) => (
                                <Button
                                    key={merchant.merchantId}
                                    variant="outlined"
                                    endIcon={<DeleteOutlineIcon />}
                                    onClick={() => {
                                        setFieldValue(
                                            'merchants',
                                            (merchants ?? []).filter((m) => m && m.merchantId !== merchant.merchantId)
                                        );
                                    }}
                                >
                                    {merchant.name}
                                </Button>
                            ))}
                    </Box>
                    <CommonTable
                        searchBox={SEARCH_BOX}
                        columnList={MERCHANT_TABLE_HEAD}
                        dataList={filteredMerchantList}
                        isLoading={isLoading}
                        tableRowKey={'merchantId'}
                        RowBodyComponent={APPSectionFormTableRow}
                        rowBodyProps={{
                            merchants: (merchants ?? []).filter((m): m is Merchant => m !== null),
                            checkBoxOnChange
                        }}
                        sx={{ minWidth: '100%', maxHeight: '100%' }}
                    />
                </Box>
            )}
            <Box
                sx={{
                    display: 'flex',
                    justifyContent: 'flex-end',
                    marginTop: 'auto'
                }}
            >
                <LoadingButton
                    loading={isLoading}
                    onClick={() => {
                        setIsDialogOpen(false);
                    }}
                >
                    Close
                </LoadingButton>
                <LoadingButton loading={isLoading} onClick={() => createSection()}>
                    Save
                </LoadingButton>
            </Box>
        </Box>
    );
};
